import { Component, DestroyRef, EventEmitter, HostListener, Input, OnInit, Output, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, forkJoin } from 'rxjs';
import { UploadFile } from 'src/app/file/model/file.model';
import { FormService } from 'src/app/form/form.service';
import { FormDetails, FormSend } from 'src/app/form/model/form.model';
import { NotificationType } from 'src/app/notifications/notification.model';
import { NotificationService } from 'src/app/notifications/notification.service';
import { formCategoryList } from 'src/app/utils/enums/enums/form/form-category.enum';
import { notify, notifyError } from '../../../utils/function/notification.util';

import { UnsavedChangesPopupComponent } from 'src/app/utils/popup/unsaved-changes-popup/unsaved-changes-popup.component';

@Component({
  selector: 'edit-form-popup',
  templateUrl: './edit-form-popup.component.html',
  styleUrl: './edit-form-popup.component.scss',
  standalone: false
})
export class EditFormPopupComponent implements OnInit {

  @Input() isModalVisible = false;

  @Input()
  set uuidForm(uuidForm: string) {
    if (uuidForm !== '') {
      this._uuidForm = uuidForm;
      this.initializeData();
    }
  }

  @Output() stateVisibleModal = new EventEmitter<boolean>();
  @Output() updateForm = new EventEmitter<FormSend>();

  readonly formCategoryList = formCategoryList;
  readonly clearUploadEventSubject = new Subject<void>();

  form!: FormGroup;
  @ViewChild(UnsavedChangesPopupComponent) unsavedChangesPopup?: UnsavedChangesPopupComponent;

  get hasPendingChanges(): boolean {
    return this.form.dirty || this.updatedFile || !!this.fileForm;
  }
  fileInformationInit?: UploadFile;
  formIDExistenceError = false;

  private _uuidForm = '';
  private fileForm?: File;
  private updatedFile = false;
  private updatedInformation = false;
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private formService: FormService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeFormGroup();
    this.initializeSubscription();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isModalVisible) this.popupClose();
  }

  popupClose(saved:boolean = false): void {
    if (!saved && this.hasPendingChanges) {
      if (!this.unsavedChangesPopup) return;
      this.unsavedChangesPopup.hasUnsavedChanges = true;
      this.unsavedChangesPopup.confirmLeave().subscribe(leave => {
        if (leave) this.finishClose();
      });
      return;
    }
    this.finishClose();
  }

  private finishClose(): void {
    this.form.reset();
    this.clearUploadEventSubject.next();
    this.fileForm = undefined;
    this.fileInformationInit = undefined;
    this.updatedFile = false;
    this.updatedInformation = false;
    this.formIDExistenceError = false;
    this.stateVisibleModal.emit(false);
  }

  setFileUploaded(event: UploadFile): void {
    this.fileForm = event.file;
    this.updatedFile = true;
  }

  /** Enabled only when the form is valid and something actually changed. */
  isButtonDisable(): boolean {
    return this.form.invalid || (!this.updatedFile && !this.updatedInformation);
  }

  onClickValidate(): void {
    if (this.isButtonDisable()) return;

    this.formService.checkExistenceTeamIDForUpdate(this.form.get('formID')?.value, this._uuidForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((exists: boolean) => {
        this.formIDExistenceError = exists;
        if (!exists) this.updateFormFunc();
      });
  }

  // =============== Private ===============

  private initializeFormGroup(): void {
    this.form = this.fb.group({
      formID:      ['', Validators.required],
      name:        ['', Validators.required],
      category:    ['', Validators.required],
      description: ['', Validators.required]
    });
  }

  private initializeSubscription(): void {
    // Any user edit marks the information as dirty (patchValue below uses emitEvent: false)
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatedInformation = true;
        this.formIDExistenceError = false;
      });
  }

  private initializeData(): void {
    this.formService.getFormDetails(this._uuidForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: FormDetails) => {
        // emitEvent: false → prefilling doesn't mark the form as "updated"
        this.form.patchValue({
          formID: value.formID,
          name: value.name,
          category: value.category,
          description: value.description
        }, { emitEvent: false });
        this.fileInformationInit = this.buildUploadFile(value);
        this.updatedInformation = false;
        this.updatedFile = false;
      });
  }

  private buildUploadFile(form: FormDetails): UploadFile {
    return {
      uuid: '',
      uploaded: true,
      name: form.fileName,
      size: form.fileSize,
      type: form.fileType,
      file: undefined
    };
  }

  private buildFormSend(): FormSend {
    return {
      uuid: this._uuidForm,
      formID: this.form.get('formID')?.value,
      name: this.form.get('name')?.value,
      category: this.form.get('category')?.value,
      description: this.form.get('description')?.value
    };
  }

  /** Runs only the needed calls (info and/or file), then notifies once both are done. */
  private updateFormFunc(): void {
    const formSend = this.buildFormSend();
    const requests: Observable<unknown>[] = [];

    if (this.updatedInformation) {
      requests.push(this.formService.updateForm(formSend, this._uuidForm));
    }
    if (this.updatedFile && this.fileForm) {
      requests.push(this.formService.uploadFileToForm(this._uuidForm, this.fileForm));
    }

    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.updatedInformation) this.updateForm.emit(formSend);
          notify(this.notificationService, NotificationType.SUCCESS, 'edit-form-popup.notification.updated');
          this.popupClose(true);
        },
        error: (err) => {
          notifyError(this.notificationService, NotificationType.ERROR, 'edit-form-popup.notification.error', err);
        }
      });
  }
}
